import { useCallback, useDeferredValue, useMemo, useState } from "react";
import { Badge, Button, Col, Form, Nav, Row, Tab } from "react-bootstrap";
import type { Tournament } from "../../shared/types";
import { buildSetListData } from "../util/setData";
import { latestPlausibleStartMs } from "../../shared/tournamentDate";
import {
  buildCharacterPlayerUsage,
  buildCharacterUsage,
  buildEntrants,
  buildStageUsage,
  buildTournamentTotals,
  countEntrants,
} from "../util/tournamentStats";
import { usePlayerLookup } from "../hooks/usePlayerLookup";
import { SetList } from "./Sets";
import { SetInfo } from "./SetInfo";
import TournamentPlayers from "./TournamentPlayers";
import TournamentStats from "./TournamentStats";

interface FocusedTournamentProps {
  tournament: Tournament;
  onBack: () => void;
  onOpenPlayer: (publicId: string) => void;
}

// A full-screen view dedicated to a single tournament, reached via its own URL
// (/<slug>). Reuses the catalog's SetList + SetInfo; on narrow screens it acts
// as a master-detail (list, then the selected set fills the view).
export default function FocusedTournament({
  tournament,
  onBack,
  onOpenPlayer,
}: FocusedTournamentProps) {
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);

  const sets = useMemo(
    () => buildSetListData(tournament, deferredSearch),
    [tournament, deferredSearch],
  );
  const selectedSet = sets.find((s) => s.id === selectedSetId) ?? null;

  const handleSelectSet = useCallback((id: string) => {
    setSelectedSetId((prev) => (prev === id ? null : id));
  }, []);

  // Tournament-level header metadata, derived the same way the catalog does.
  const latestMs = latestPlausibleStartMs(tournament.sets.map((s) => s.context.startMs));
  const date = latestMs > 0 ? new Date(latestMs).toLocaleDateString() : "";
  const location = tournament.sets[0]?.context.startgg.tournament.location ?? "";

  // Derived tournament aggregations for the Players and Stats tabs.
  const entrantEvents = useMemo(() => buildEntrants(tournament), [tournament]);
  const entrantTotal = useMemo(() => countEntrants(tournament), [tournament]);
  const characterUsage = useMemo(() => buildCharacterUsage(tournament), [tournament]);
  const characterByPlayer = useMemo(() => buildCharacterPlayerUsage(tournament), [tournament]);
  const stageUsage = useMemo(() => buildStageUsage(tournament), [tournament]);
  const totals = useMemo(() => buildTournamentTotals(tournament), [tournament]);

  // Which raw entrant names already resolve to a canonical player page, so
  // TournamentPlayers can make them clickable - kept separate from tournament
  // loading (filesystem-only) since it depends on the entrants database.
  const lookupNames = useMemo(
    () => Array.from(new Set(entrantEvents.flatMap((e) => e.entrants.map((en) => en.name)))),
    [entrantEvents],
  );
  const playerLookup = usePlayerLookup(tournament.folder, lookupNames);

  const playlistUrl = tournament.playlistId
    ? `https://www.youtube.com/playlist?list=${tournament.playlistId}`
    : null;

  // start.gg tournament page, derived from a set's event slug
  // ("tournament/<slug>/event/<eventSlug>" → start.gg/tournament/<slug>").
  const tournamentSlug = (tournament.sets[0]?.context.startgg.event.slug ?? "").split("/event/")[0];
  const startggUrl = tournamentSlug.startsWith("tournament/")
    ? `https://www.start.gg/${tournamentSlug}`
    : null;

  const [copied, setCopied] = useState(false);
  const copyLink = useCallback(() => {
    navigator.clipboard?.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, []);

  const setsCard = (
    <div className="h-100 bg-body-secondary rounded d-flex flex-column overflow-hidden pb-3">
      <h5 className="px-3 py-2 mb-0 border-bottom text-center">
        Sets
        <Badge bg="secondary" className="ms-2">
          {sets.length}
        </Badge>
      </h5>
      <div className="px-3 pt-2">
        <Form.Control
          size="sm"
          type="text"
          placeholder="Search players..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="flex-grow-1 overflow-auto px-3 pt-3">
        {sets.length === 0 ? (
          <p className="text-muted py-3 text-center">No matching sets</p>
        ) : (
          <SetList sets={sets} selectedSetId={selectedSetId} onSelectSet={handleSelectSet} />
        )}
      </div>
    </div>
  );

  const setInfoCard = (
    <div className="h-100 bg-body-secondary rounded d-flex flex-column overflow-hidden pb-3">
      <h5 className="px-3 py-2 mb-0 border-bottom text-center">Set Info</h5>
      <div className="flex-grow-1 overflow-auto px-3 pt-3">
        {selectedSet ? (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              className="mb-2 d-lg-none"
              onClick={() => setSelectedSetId(null)}
            >
              <i className="bi bi-arrow-left me-1" />
              Back to Sets
            </Button>
            <SetInfo
              key={selectedSet.id}
              set={selectedSet}
              playerLookup={playerLookup}
              onOpenPlayer={onOpenPlayer}
            />
          </>
        ) : (
          <p className="text-muted py-3 text-center">Select a set to view details</p>
        )}
      </div>
    </div>
  );

  return (
    <main className="flex-grow-1 overflow-hidden d-flex flex-column p-3 gap-3">
      {/* Header band: from lg up, name/date/counts stack is truly centered in
          the box while back + actions are floated to the top corners so they sit
          inline with the tournament name without shifting the centering. Below
          lg the corners can't fit alongside the title, so the band becomes a
          plain vertical stack (back, title, actions) - see .tournament-header in
          App.css. */}
      <div className="tournament-header bg-body-secondary rounded px-3 py-2 position-relative d-flex flex-column align-items-center gap-2">
        <Button
          variant="outline-secondary"
          size="sm"
          className="tournament-header-back align-self-start flex-shrink-0"
          onClick={onBack}
        >
          <i className="bi bi-arrow-left me-1" />
          All tournaments
        </Button>

        <div className="text-center px-2">
          <h3 className="mb-1 text-truncate" title={tournament.folder}>
            {tournament.folder}
          </h3>
          {(date || location) && (
            <div className="text-body-secondary fs-5 mb-1">
              {[date, location].filter(Boolean).join(" · ")}
            </div>
          )}
          <div className="text-body-secondary fs-6">
            {entrantTotal} entrants · {tournament.sets.length} sets
          </div>
        </div>

        <div className="tournament-header-actions d-flex flex-wrap gap-2 justify-content-center">
          {startggUrl && (
            <Button
              as="a"
              href={startggUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline-secondary"
              size="sm"
              className="flex-shrink-0 d-inline-flex align-items-center gap-1"
              title="View tournament on start.gg (opens in new tab)"
            >
              start.gg
              <i className="bi bi-box-arrow-up-right" />
            </Button>
          )}
          {playlistUrl && (
            <Button
              as="a"
              href={playlistUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="outline-danger"
              size="sm"
              className="flex-shrink-0 d-inline-flex align-items-center gap-1"
            >
              <i className="bi bi-collection-play-fill" />
              Playlist
            </Button>
          )}
          {totals.games > 0 && (
            <Button
              as="a"
              href={`/api/download-tournament/${encodeURIComponent(tournament.folder)}`}
              download
              variant="outline-secondary"
              size="sm"
              className="flex-shrink-0 d-inline-flex align-items-center gap-1"
              title="Download all replays as a single zip"
            >
              <i className="bi bi-download" />
              Download all
            </Button>
          )}
          <Button
            variant="outline-secondary"
            size="sm"
            className="flex-shrink-0 d-inline-flex align-items-center gap-1"
            onClick={copyLink}
          >
            <i className={`bi ${copied ? "bi-check-lg" : "bi-link-45deg"}`} />
            {copied ? "Copied" : "Copy link"}
          </Button>
        </div>
      </div>

      <Tab.Container defaultActiveKey="sets" mountOnEnter>
        <Nav variant="tabs" className="flex-shrink-0">
          <Nav.Item>
            <Nav.Link eventKey="sets">Sets</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="overview">Overview</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content className="flex-grow-1 overflow-hidden">
          {/* Sets: master-detail. Both panes show side-by-side from lg up; below
              lg only one is visible at a time depending on whether a set is
              selected. */}
          <Tab.Pane eventKey="sets" className="h-100 overflow-hidden">
            <Row className="h-100 g-3">
              <Col
                xs={12}
                lg={5}
                className={`h-100 overflow-hidden ${selectedSet ? "d-none d-lg-block" : ""}`}
              >
                {setsCard}
              </Col>
              <Col
                xs={12}
                lg={7}
                className={`h-100 overflow-hidden ${selectedSet ? "" : "d-none d-lg-block"}`}
              >
                {setInfoCard}
              </Col>
            </Row>
          </Tab.Pane>
          <Tab.Pane eventKey="overview" className="h-100 d-flex flex-column overview-pane">
            <p className="text-body-secondary small mb-3 flex-shrink-0">
              <i className="bi bi-info-circle me-1" />
              Information is based only on sets that were recorded.
            </p>
            {/* gy-lg-0: drop the vertical gutter at lg+ so each height:100%
                column fits the pane exactly (the gutter margin-top would push
                the column past the pane bottom and clip its last row). */}
            <Row className="g-4 gy-lg-0 overview-row">
              <Col lg={6} className="overview-col">
                <TournamentPlayers
                  events={entrantEvents}
                  playerLookup={playerLookup}
                  onOpenPlayer={onOpenPlayer}
                />
              </Col>
              <Col lg={6} className="overview-col">
                <TournamentStats
                  totals={totals}
                  characterUsage={characterUsage}
                  characterByPlayer={characterByPlayer}
                  stageUsage={stageUsage}
                />
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </main>
  );
}
